import { supabase } from '@/integrations/supabase/client';
import { latestSsoAssertionAtSeconds } from '@/shared/ssoSession';

/**
 * ENT-4204 SSO step-up Phase 2 — shared re-assertion helper.
 *
 * When a step-up gate returns `sso_reassertion_required`, the UI re-runs SSO
 * login so the IdP mints a fresh assertion, then returns the user to the route
 * they were on to retry. This single implementation is used by the step-up
 * dialog and every bypass surface (no copy-paste).
 *
 * Return-target policy (spec D-c): the only proven SSO return target is the
 * allow-listed root `${origin}/?sso_return=1`. We append `stepup_resume=<path>`
 * so `Index.tsx` can navigate back to the originating route after the handshake.
 * We NEVER pass an arbitrary deep link as `redirectTo`.
 */

/** Prefix GoTrue stamps on `app_metadata.provider` for an SSO identity. */
const SSO_PROVIDER_PREFIX = 'sso:';

/**
 * One-shot sessionStorage marker (ENT-4204 Codex P1/P2). `startSsoReassertion`
 * sets it immediately before redirecting to the IdP; on return, whichever auth
 * branch first observes the fresh session consumes it to drive the resume. It is
 * the proof of a genuine re-assertion initiated in THIS tab:
 *  - it survives the cross-origin IdP round-trip (sessionStorage is per-origin,
 *    preserved across navigation away and back within the same tab);
 *  - a direct visit or a stale cached session never has it, so neither resumes
 *    (satisfies "require a callback-created session");
 *  - consuming it is atomic (read-then-remove), so exactly one branch resumes and
 *    the other cannot race a second navigation (satisfies "no routing race").
 */
const STEPUP_REASSERT_MARKER = 'sg.sso.stepupReassertPending';

/**
 * READ (without clearing) the one-shot re-assertion marker. Returns the latest
 * `sso/saml` AMR assertion timestamp (epoch seconds) captured immediately BEFORE
 * the IdP redirect, or null when no re-assertion is pending.
 *
 * Peek — NOT read-and-clear (ENT-4204 Codex round-9 P1). On return, the callback
 * page may observe the CACHED pre-redirect session (same, non-newer assertion)
 * before the freshly-exchanged one arrives. A destructive read-then-delete would
 * remove the marker during that first non-qualifying observation, so the later
 * `SIGNED_IN` carrying the genuinely-fresh assertion would find no marker and
 * never resume the guarded action. The caller therefore PEEKS here, compares
 * freshness, and only `clearStepUpReassertionMarker()`s once a strictly-newer
 * assertion actually arrives — retaining the marker across non-qualifying
 * observations. Exactly-one-resume is still guaranteed by the caller's one-shot
 * ref guard, not by clearing the marker.
 *
 * The caller resumes only when the session observed on return carries a
 * strictly-newer SSO assertion timestamp — proving a genuinely fresh IdP
 * assertion (ENT-4204 Codex round-7 P2). A silent token-exchange failure that
 * leaves the stale session, OR a mere background token REFRESH, does NOT advance
 * the AMR timestamp, so neither is resumed (a refresh advances `expires_at` but
 * carries the original assertion forward, which is why AMR timestamp — not
 * expiry — is the anchor).
 */
export function peekStepUpReassertionMarker(): number | null {
  try {
    const raw = sessionStorage.getItem(STEPUP_REASSERT_MARKER);
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return null;
  }
}

/**
 * Clear the one-shot re-assertion marker. Called by the resume handler ONLY after
 * a qualifying (strictly-newer) SSO assertion has arrived and the resume
 * navigation is committed, so a non-qualifying early observation never discards
 * the pending marker (ENT-4204 Codex round-9 P1).
 */
export function clearStepUpReassertionMarker(): void {
  try {
    sessionStorage.removeItem(STEPUP_REASSERT_MARKER);
  } catch {
    // sessionStorage unavailable — nothing to clear; the marker (if any) dies
    // with the tab. Harmless: resume also requires the `stepup_resume` URL param.
  }
}

/**
 * Build the allow-listed SSO return URL carrying a `stepup_resume` marker so the
 * app can navigate back to `pathname + search` after re-authentication.
 */
export function buildStepUpResumeRedirect(
  origin: string,
  pathname: string,
  search: string,
): string {
  const resume = encodeURIComponent(`${pathname}${search}`);
  return `${origin}/?sso_return=1&stepup_resume=${resume}`;
}

/**
 * Resolve the current session's SSO provider id (the `app_metadata.provider`
 * with the `sso:` prefix stripped), or null when the session is not SSO. Keying
 * on `providerId` (not email domain) is spec decision D-b: an org's IdP may
 * legitimately assert a non-domain invitee email (ENT-4202).
 */
export async function resolveSsoProviderId(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  const provider = data.session?.user?.app_metadata?.provider;
  if (typeof provider !== 'string' || !provider.startsWith(SSO_PROVIDER_PREFIX)) {
    return null;
  }
  const providerId = provider.slice(SSO_PROVIDER_PREFIX.length);
  return providerId.length > 0 ? providerId : null;
}

/**
 * Validate a `stepup_resume` query value into a safe same-origin path to
 * navigate to on return, or null when it is unsafe. Open-redirect guard: the
 * target must be a root-relative path (`/…`) and must NOT be protocol-relative
 * (`//host`) or a backslash-smuggled variant (`/\host`). Never returns an
 * absolute URL, so a crafted `stepup_resume` cannot bounce the user off-origin.
 */
export function safeStepUpResumeTarget(
  raw: string | null | undefined,
): string | null {
  if (!raw) return null;
  // The caller reads this via `URLSearchParams.get('stepup_resume')`, which has
  // ALREADY percent-decoded the value (ENT-4204 Codex P2). Do NOT decode again —
  // a second decode would corrupt legitimately-encoded characters in the
  // originating route (e.g. an `%26` inside a nested query becoming a `&`
  // delimiter). Validate the already-decoded path as-is.
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//') || raw.startsWith('/\\')) return null;
  return raw;
}

export interface StartSsoReassertionResult {
  /** True once the browser has been sent to the IdP (page is navigating away). */
  redirected: boolean;
  /** Present when the re-assertion could not be started. */
  error?: string;
}

/**
 * Re-run SSO login for the current session's provider and redirect the browser
 * to the IdP. On return, `Index.tsx` consumes `stepup_resume` and sends the user
 * back to the originating route with a "please retry" toast. Never auto-replays
 * the gated mutation.
 */
export async function startSsoReassertion(): Promise<StartSsoReassertionResult> {
  // ENT-4204 (Codex P2): ALWAYS honour the StartSsoReassertionResult contract —
  // never reject. If `getSession`/`signInWithSSO` throw (transport, storage, or
  // client failure) the caller (e.g. StepUpAal2Dialog.handleSsoReassert) would
  // otherwise be left with `submitting` stuck true, both buttons disabled, and no
  // error shown. Catch everything and return `{ redirected: false, error }`.
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const currentSession = sessionData.session;
    const provider = currentSession?.user?.app_metadata?.provider;
    const providerId =
      typeof provider === 'string' && provider.startsWith(SSO_PROVIDER_PREFIX)
        ? provider.slice(SSO_PROVIDER_PREFIX.length)
        : '';
    if (!providerId) {
      return {
        redirected: false,
        error: 'This account is not linked to a single sign-on provider.',
      };
    }
    // Freshness anchor: the CURRENT session's latest `sso/saml` AMR assertion
    // timestamp. On return we resume only if the observed session carries a
    // strictly-newer assertion timestamp (a genuinely fresh IdP assertion) —
    // never on a stale token or a mere refresh, both of which keep this value.
    const prevAssertionAt = latestSsoAssertionAtSeconds(currentSession) ?? 0;

    const redirectTo = buildStepUpResumeRedirect(
      window.location.origin,
      window.location.pathname,
      window.location.search,
    );

    const { data, error } = await supabase.auth.signInWithSSO({
      providerId,
      options: { redirectTo, skipBrowserRedirect: true },
    });

    if (error || !data?.url) {
      return {
        redirected: false,
        error: error?.message ?? 'Unable to start single sign-on. Please try again.',
      };
    }

    // Record the pre-redirect assertion timestamp so the return handler can
    // require a strictly-newer SSO assertion (a fresh IdP login) before resuming.
    try {
      sessionStorage.setItem(STEPUP_REASSERT_MARKER, String(prevAssertionAt));
    } catch {
      // sessionStorage unavailable — the return handler falls back to not
      // resuming (fail-safe: the user simply re-triggers the action manually).
    }
    window.location.assign(data.url);
    return { redirected: true };
  } catch (err) {
    return {
      redirected: false,
      error: err instanceof Error ? err.message : 'Unable to start single sign-on. Please try again.',
    };
  }
}
